import type { AccountNode } from '@taler/api-client'
import { type Locale, resolveLocalized } from '@taler/domain'

export interface AccountOption {
  readonly id: string
  readonly code: string
  readonly label: string
  readonly currency: string | undefined
}

function flatten(
  nodes: readonly AccountNode[],
  locale: Locale,
  matches: (node: AccountNode) => boolean,
): readonly AccountOption[] {
  const out: AccountOption[] = []
  const walk = (node: AccountNode): void => {
    if (node.account.isPostable && matches(node)) {
      out.push({
        id: node.account.id,
        code: node.account.code,
        label: resolveLocalized(node.account.names, locale)?.name ?? node.account.code,
        currency: node.account.currency,
      })
    }
    for (const child of node.children) walk(child)
  }
  for (const node of nodes) walk(node)
  return out
}

/** The expense side of a car event's journal entry - any expense-class account, not just `EXPENSE.CAR`'s
 * own children (AGENTS.md: "nothing forces a car event's expense posting to land on a car-typed account"). */
export function flattenExpenseAccounts(
  nodes: readonly AccountNode[],
  locale: Locale,
): readonly AccountOption[] {
  return flatten(nodes, locale, (node) => node.account.accountClass === 'EXPENSE')
}

/** The money side - any postable asset or liability leaf, in any currency; the event's own
 * amount is entered in whichever currency the chosen account carries. */
export function flattenMoneyAccounts(
  nodes: readonly AccountNode[],
  locale: Locale,
): readonly AccountOption[] {
  return flatten(
    nodes,
    locale,
    (node) => node.account.accountClass === 'ASSET' || node.account.accountClass === 'LIABILITY',
  )
}

/** The proceeds side of a SELL event's journal entry - money coming in belongs against an
 * income account, never an expense category. */
export function flattenIncomeAccounts(
  nodes: readonly AccountNode[],
  locale: Locale,
): readonly AccountOption[] {
  return flatten(nodes, locale, (node) => node.account.accountClass === 'INCOME')
}
